import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fieldError, type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDateTime } from '../../lib/format';
import type { WebhookDelivery, WebhookEndpoint } from '../../lib/types';
import {
  Badge,
  Button,
  Card,
  DataTable,
  ErrorBanner,
  FormField,
  Input,
  Modal,
  PageHeader,
  Spinner,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';

const AVAILABLE_EVENTS = [
  'order.paid',
  'order.refunded',
  'ticket.issued',
  'ticket.checked_in',
  'ticket.transferred',
  'waitlist.notified',
];

export function WebhooksPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const endpointsQuery = useQuery({
    queryKey: ['webhook-endpoints'],
    queryFn: api.webhookEndpoints.list,
  });
  const deliveriesQuery = useQuery({
    queryKey: ['webhook-deliveries'],
    queryFn: () => api.webhookDeliveries.list({ limit: 50 }),
  });

  return (
    <div>
      <PageHeader
        title="Webhooks"
        description="Register endpoints to receive order and ticket events."
        actions={
          <RequirePermission permission="webhooks.manage">
            <Button onClick={() => setCreateOpen(true)}>Add endpoint</Button>
          </RequirePermission>
        }
      />
      <Card title="Endpoints" className="mb-6">
        {endpointsQuery.isLoading ? (
          <Spinner />
        ) : endpointsQuery.error ? (
          <ErrorBanner
            message={
              endpointsQuery.error instanceof Error
                ? endpointsQuery.error.message
                : 'Failed to load webhook endpoints.'
            }
          />
        ) : (
          <DataTable<WebhookEndpoint>
            columns={[
              {
                key: 'url',
                header: 'URL',
                render: (w) => <span className="font-mono text-xs">{w.url}</span>,
              },
              { key: 'events', header: 'Events', render: (w) => w.events.join(', ') },
              {
                key: 'enabled',
                header: 'Enabled',
                render: (w) =>
                  w.enabled ? <Badge tone="success">Yes</Badge> : <Badge tone="neutral">No</Badge>,
              },
              { key: 'createdAt', header: 'Created', render: (w) => formatDateTime(w.createdAt) },
            ]}
            rows={endpointsQuery.data?.data ?? []}
            getRowKey={(w) => w.id}
            emptyMessage="No webhook endpoints registered yet."
          />
        )}
      </Card>
      <Card title="Recent deliveries">
        {deliveriesQuery.isLoading ? (
          <Spinner />
        ) : (
          <DataTable<WebhookDelivery>
            columns={[
              { key: 'eventType', header: 'Event', render: (d) => d.eventType },
              {
                key: 'status',
                header: 'Status',
                render: (d) => (
                  <Badge
                    tone={
                      d.status === 'delivered'
                        ? 'success'
                        : d.status === 'pending'
                          ? 'neutral'
                          : 'danger'
                    }
                  >
                    {d.status}
                  </Badge>
                ),
              },
              { key: 'attempts', header: 'Attempts', render: (d) => d.attemptCount },
              {
                key: 'responseStatus',
                header: 'HTTP status',
                render: (d) => d.responseStatus ?? '-',
              },
              {
                key: 'lastAttempt',
                header: 'Last attempt',
                render: (d) => formatDateTime(d.lastAttemptAt),
              },
            ]}
            rows={deliveriesQuery.data?.data ?? []}
            getRowKey={(d) => d.id}
            emptyMessage="No deliveries yet."
          />
        )}
      </Card>
      <CreateWebhookModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={setRevealedSecret}
      />
      <Modal
        open={!!revealedSecret}
        onClose={() => setRevealedSecret(null)}
        title="Endpoint signing secret"
      >
        <p className="text-sm text-slate-600 mb-2">
          Copy this now to verify webhook signatures - it will not be shown again.
        </p>
        <code className="block break-all bg-slate-100 rounded-brand p-3 text-sm">
          {revealedSecret}
        </code>
      </Modal>
    </div>
  );
}

function CreateWebhookModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (secret: string) => void;
}) {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const mutation = useMutation({
    mutationFn: () => api.webhookEndpoints.create({ url, events }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['webhook-endpoints'] });
      onClose();
      onCreated(created.secret);
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  function toggle(evt: string) {
    setEvents((prev) => (prev.includes(evt) ? prev.filter((e) => e !== evt) : [...prev, evt]));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add webhook endpoint"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!url.trim() || events.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <FormField label="URL" error={fieldError(formError, 'url')}>
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/webhooks/loctopus"
        />
      </FormField>
      <FormField label="Events">
        <div className="space-y-1">
          {AVAILABLE_EVENTS.map((evt) => (
            <label key={evt} className="flex items-center gap-2 text-sm font-mono">
              <input type="checkbox" checked={events.includes(evt)} onChange={() => toggle(evt)} />
              {evt}
            </label>
          ))}
        </div>
      </FormField>
    </Modal>
  );
}
